import type { TipTapDoc, TipTapNode } from "../schema/tiptapDoc";

/**
 * Render a TipTap document as readable plain text — for OS share sheets,
 * clipboard, and other targets that can't take HTML. Headings become their
 * own lines, bullets use "•", ordered lists are numbered, tables become
 * pipe-separated rows, and emphasis marks are dropped (no literal `**`).
 */
export function tipTapToPlainText(doc: TipTapDoc | null | undefined): string {
    if (!doc || !Array.isArray(doc.content)) return "";
    const out = doc.content.map((node) => renderBlock(node, 0)).filter((s) => s.length > 0);
    return out
        .join("\n\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

function renderInline(nodes: TipTapNode[] | undefined): string {
    if (!nodes) return "";
    return nodes
        .map((n) => {
            switch (n.type) {
                case "text": {
                    const link = n.marks?.find((m) => m.type === "link");
                    const href = link && "attrs" in link ? link.attrs?.href : undefined;
                    return href && href !== n.text ? `${n.text} (${href})` : n.text;
                }
                case "hardBreak":
                    return "\n";
                default:
                    return renderInline("content" in n ? n.content : undefined);
            }
        })
        .join("");
}

function renderBlock(node: TipTapNode, depth: number): string {
    const indent = "  ".repeat(depth);
    switch (node.type) {
        case "paragraph":
            return indent + renderInline(node.content);
        case "heading":
            return indent + renderInline(node.content).toUpperCase();
        case "blockquote":
            return node.content
                .map((c) => renderBlock(c, depth))
                .filter(Boolean)
                .map((line) => `${indent}> ${line.trimStart()}`)
                .join("\n");
        case "bulletList":
            return node.content.map((li) => `${indent}• ${renderListItem(li, depth)}`).join("\n");
        case "orderedList": {
            const start = node.attrs?.start ?? 1;
            return node.content
                .map((li, i) => `${indent}${start + i}. ${renderListItem(li, depth)}`)
                .join("\n");
        }
        case "listItem":
            return renderListItem(node, depth);
        case "horizontalRule":
            return `${indent}———`;
        case "table":
            return node.content.map((row) => renderBlock(row, depth)).join("\n");
        case "tableRow":
            return (
                indent +
                node.content
                    .map((cell) => ("content" in cell ? renderInlineBlocks(cell.content) : ""))
                    .join(" | ")
            );
        case "tableCell":
        case "tableHeader":
            return indent + renderInlineBlocks(node.content);
        case "image":
            return node.attrs.alt ? `${indent}[Image: ${node.attrs.alt}]` : "";
        case "text":
        case "hardBreak":
            return indent + renderInline([node]);
        default:
            // mediaEmbed and any future block types: fall back to their text.
            return "content" in node && Array.isArray(node.content)
                ? renderInlineBlocks(node.content)
                : "";
    }
}

/** A list item's first paragraph inline, nested blocks on following lines. */
function renderListItem(li: TipTapNode, depth: number): string {
    const children = "content" in li && Array.isArray(li.content) ? li.content : [];
    const [first, ...rest] = children;
    const head = first ? renderBlock(first, 0).trimStart() : "";
    const tail = rest.map((c) => renderBlock(c, depth + 1)).filter(Boolean);
    return [head, ...tail].join("\n");
}

function renderInlineBlocks(nodes: TipTapNode[] | undefined): string {
    if (!nodes) return "";
    return nodes
        .map((n) => renderBlock(n, 0))
        .filter(Boolean)
        .join(" ");
}
